/**
 * Formatage d'affichage des données d'identité : nom, prénom et téléphone.
 *
 * Règle de nommage retenue dans toute l'application : le **nom de famille** s'affiche en
 * capitales et le **prénom** avec une capitale initiale, quelle que soit la casse saisie
 * (`jean-pierre DUPONT` → `Jean-Pierre DUPONT`). Les numéros de téléphone sont présentés par
 * groupes de deux chiffres afin de rester lisibles (`0101010101` → `01 01 01 01 01`).
 *
 * Ces fonctions ne lèvent jamais : une valeur absente rend une chaîne vide, ce qui laisse
 * chaque appelant appliquer sa propre valeur de repli (référence, e-mail, tiret…).
 */
/** Séparateurs internes d'un prénom composé : début de chaîne, espace, tiret ou apostrophe. */
const FIRST_NAME_SEPARATOR = /(^|[\s\-'’])(\S)/g;
/** Met un nom de famille en capitales, quelle que soit la casse saisie. */
export function toLastNameCase(value) {
    return (value ?? '').trim().toLocaleUpperCase('fr-FR');
}
/**
 * Met un prénom en capitale initiale, le reste en minuscules. Chaque composant d'un prénom
 * composé est traité séparément (`jean-pierre` → `Jean-Pierre`, `marie claire` → `Marie Claire`).
 */
export function toFirstNameCase(value) {
    const trimmed = (value ?? '').trim();
    if (trimmed.length === 0) {
        return '';
    }
    return trimmed
        .toLocaleLowerCase('fr-FR')
        .replace(FIRST_NAME_SEPARATOR, (_match, separator, letter) => `${separator}${letter.toLocaleUpperCase('fr-FR')}`);
}
/** Assemble une identité au format `Prénom NOM` ; chaîne vide si aucune des deux parties n'est renseignée. */
export function formatPersonName(firstName, lastName) {
    return [toFirstNameCase(firstName), toLastNameCase(lastName)]
        .filter((part) => part.length > 0)
        .join(' ');
}
/** Assemble une identité au format `NOM Prénom` (listes professionnelles, documents). */
export function formatPersonNameLastFirst(lastName, firstName) {
    return [toLastNameCase(lastName), toFirstNameCase(firstName)]
        .filter((part) => part.length > 0)
        .join(' ');
}
/**
 * Variante de [`toFirstNameCase`] destinée à la **saisie** : la distinction entre `null`
 * (valeur effacée) et `undefined` (champ non transmis) est préservée, afin de ne pas transformer
 * une mise à jour partielle en effacement.
 */
export function normalizeFirstName(value) {
    return value === null || value === undefined ? value : toFirstNameCase(value);
}
/** Variante de [`toLastNameCase`] destinée à la saisie, aux mêmes conditions que [`normalizeFirstName`]. */
export function normalizeLastName(value) {
    return value === null || value === undefined ? value : toLastNameCase(value);
}
/**
 * Présente un numéro de téléphone par groupes de deux chiffres, quel que soit le format saisi.
 * Un préfixe international est conservé (`+33612345678` → `+33 61 23 45 67 8`). Une valeur
 * dépourvue de chiffres est rendue telle quelle afin de ne jamais masquer une saisie libre.
 */
export function formatPhoneNumber(value) {
    const trimmed = (value ?? '').trim();
    if (trimmed.length === 0) {
        return '';
    }
    const digits = trimmed.replace(/\D/g, '');
    if (digits.length === 0) {
        return trimmed;
    }
    const grouped = digits.replace(/(..)(?=.)/g, '$1 ');
    return trimmed.startsWith('+') ? `+${grouped}` : grouped;
}
//# sourceMappingURL=format.utils.js.map